import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Megaphone, PencilSimple, Trash, Calendar, CurrencyDollar, Package,
  Bank, ChartLineUp, StopCircle, TiktokLogo, FacebookLogo, InstagramLogo, Globe,
  ClipboardText, CheckCircle, UploadSimple, X, ImageSquare, LinkSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { uploadPhoto } from "@/lib/uploadPhoto";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const addDaysISO = (base, n) => { const d = new Date(base + "T12:00:00"); d.setDate(d.getDate() + n); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

const CHANNELS = [
  { v: "facebook_marketplace", label: "FB Marketplace", color: "#1877F2", Icon: FacebookLogo },
  { v: "facebook_fanpage",     label: "FB Fanpage",     color: "#1877F2", Icon: FacebookLogo },
  { v: "tiktok",               label: "TikTok",         color: "#000000", Icon: TiktokLogo },
  { v: "instagram",            label: "Instagram",      color: "#E4405F", Icon: InstagramLogo },
  { v: "other",                label: "Otro",           color: "#7E57C2", Icon: Globe },
];

const STATUS_META = {
  scheduled:        { label: "Programado", color: "#7E57C2" },
  active:           { label: "Activo",     color: "#00873D" },
  expired:          { label: "Vencido",    color: "#E0000F" },
  finished_manual:  { label: "Finalizado", color: "#52525B" },
};

const EMPTY = {
  title: "",
  channel: "facebook_marketplace",
  custom_channel: "",
  start_date: todayISO(),
  end_date: addDaysISO(todayISO(), 7),
  cost_per_day: 100,
  currency: "MXN",
  bank_id: "",
  product_id: "",
  projected_units: 1,
  projected_unit_price: "",
  notes: "",
};

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [banks, setBanks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toFinish, setToFinish] = useState(null);
  const [resultsAd, setResultsAd] = useState(null);   // ad seleccionado para "Registrar resultados"
  const [resultsForm, setResultsForm] = useState(null);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const [savingResults, setSavingResults] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");

  const load = async () => {
    const [a, s] = await Promise.all([
      api.get("/ads"),
      api.get("/ads/stats"),
    ]);
    setAds(a.data); setStats(s.data);
  };
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/banks").then((r) => setBanks(r.data)).catch(() => setBanks([]));
  }, []);

  const openNew = () => { setEditingId(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (ad) => {
    setEditingId(ad.id);
    setForm({
      title: ad.title || "",
      channel: ad.channel,
      custom_channel: ad.custom_channel || "",
      start_date: ad.start_date,
      end_date: ad.end_date,
      cost_per_day: ad.cost_per_day,
      currency: ad.currency || "MXN",
      bank_id: ad.bank_id || "",
      product_id: ad.product_id || "",
      projected_units: ad.projected_units || 1,
      projected_unit_price: ad.projected_unit_price ?? "",
      notes: ad.notes || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Escribe el título");
    if (form.start_date > form.end_date) return toast.error("La fecha fin debe ser posterior a la de inicio");
    setSaving(true);
    try {
      const payload = {
        ...form,
        cost_per_day: parseFloat(form.cost_per_day) || 0,
        projected_units: parseInt(form.projected_units) || 0,
        projected_unit_price: form.projected_unit_price !== "" ? parseFloat(form.projected_unit_price) : null,
        bank_id: form.bank_id || null,
        product_id: form.product_id || null,
      };
      if (editingId) {
        await api.put(`/ads/${editingId}`, payload);
        toast.success("Anuncio actualizado");
      } else {
        await api.post("/ads", payload);
        toast.success("Anuncio creado");
      }
      setShowForm(false); setEditingId(null); setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/ads/${toDelete.id}`);
      toast.success("Anuncio archivado");
      setToDelete(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const confirmFinish = async () => {
    if (!toFinish) return;
    try {
      await api.post(`/ads/${toFinish.id}/finish`);
      toast.success("Anuncio marcado como finalizado");
      setToFinish(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openResults = (ad) => {
    setResultsAd(ad);
    setResultsForm({
      units_sold: ad.projected_units || 0,
      revenue_amount: ad.projected_revenue || 0,
      revenue_currency: "MXN",
      actual_spend: ad.total_budget || 0,
      actual_spend_currency: "MXN",
      messages_received: 0,
      interested_clients: 0,
      closed_deals: ad.projected_units || 0,
      comments: "",
      evidence_url: "",
      register_expense: !!ad.bank_id,
      bank_id: ad.bank_id || "",
    });
  };

  const uploadEvidence = async (file) => {
    if (!file) return;
    setUploadingEvidence(true);
    try {
      const url = await uploadPhoto(file, { folder: "alendrapc/ads/evidence" });
      setResultsForm((f) => ({ ...f, evidence_url: url }));
      toast.success("Evidencia subida");
    } catch (err) {
      toast.error(err?.message || "Error subiendo evidencia");
    } finally { setUploadingEvidence(false); }
  };

  const submitResults = async (e) => {
    e.preventDefault();
    if (!resultsForm) return;
    setSavingResults(true);
    try {
      const payload = {
        units_sold: parseInt(resultsForm.units_sold) || 0,
        revenue_amount: parseFloat(resultsForm.revenue_amount) || 0,
        revenue_currency: resultsForm.revenue_currency,
        actual_spend: parseFloat(resultsForm.actual_spend) || 0,
        actual_spend_currency: resultsForm.actual_spend_currency,
        messages_received: parseInt(resultsForm.messages_received) || 0,
        interested_clients: parseInt(resultsForm.interested_clients) || 0,
        closed_deals: parseInt(resultsForm.closed_deals) || 0,
        comments: resultsForm.comments,
        evidence_url: resultsForm.evidence_url,
        register_expense: resultsForm.register_expense,
        bank_id: resultsForm.bank_id || null,
      };
      await api.post(`/ads/${resultsAd.id}/results`, payload);
      toast.success(payload.register_expense && payload.actual_spend > 0
        ? "Resultados guardados · Gasto registrado y banco descontado"
        : "Resultados guardados");
      setResultsAd(null); setResultsForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSavingResults(false); }
  };

  // Auto-fill projected_unit_price when product changes (only if empty)
  useEffect(() => {
    if (!form.product_id || form.projected_unit_price !== "") return;
    const p = products.find((x) => x.id === form.product_id);
    if (p) {
      const price = p.min_price || p.store_price || p.marketplace_price || 0;
      if (price > 0) setForm((f) => ({ ...f, projected_unit_price: price }));
    }
  }, [form.product_id, form.projected_unit_price, products]);

  const filtered = useMemo(() => ads.filter((a) => {
    if (statusFilter !== "all" && a.status !== statusFilter) return false;
    if (channelFilter !== "all" && a.channel !== channelFilter) return false;
    return true;
  }), [ads, statusFilter, channelFilter]);

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">PUBLICIDAD</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <Megaphone size={36} weight="duotone" className="text-[#E91E63]"/> Ads
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">Simula, controla y analiza tus anuncios pagados en FB Marketplace, Fanpage, TikTok, Instagram y otros canales.</p>
        </div>
        <Button onClick={openNew} className="gap-2 bg-[#E91E63] hover:bg-[#c2185b]" data-testid="new-ad-btn"><Plus size={16}/>Nuevo anuncio</Button>
      </header>

      {/* Stats cards */}
      {stats && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ads-stats">
          <div className="border border-zinc-200 bg-white p-4 rounded-sm">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Ads activos</div>
            <div className="numeric text-3xl font-heading font-bold text-[#00873D]">{stats.counts.active}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5">{stats.counts.scheduled} programados · {stats.counts.expired} vencidos</div>
          </div>
          <div className="border border-zinc-200 bg-white p-4 rounded-sm">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Gasto de hoy</div>
            <div className="numeric text-3xl font-heading font-bold text-[#E0000F]">MX${fmt(stats.today_spend_mxn)}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5">suma de costos/día de activos</div>
          </div>
          <div className="border border-zinc-200 bg-white p-4 rounded-sm">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Presupuesto activo</div>
            <div className="numeric text-3xl font-heading font-bold text-[#002FA7]">MX${fmt(stats.active_budget_mxn)}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5">total a gastar en ads activos</div>
          </div>
          <div className="border border-zinc-200 bg-white p-4 rounded-sm">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Proyección activa</div>
            <div className="numeric text-3xl font-heading font-bold text-[#7E57C2]">MX${fmt(stats.active_projected_revenue_mxn)}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5">ingreso esperado de ads activos</div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-500">Filtros:</span>
        <div className="inline-flex border border-zinc-200 rounded overflow-hidden text-xs" data-testid="filter-status">
          {[["all", "Todos"], ["active", "Activos"], ["scheduled", "Programados"], ["expired", "Vencidos"], ["finished_manual", "Finalizados"]].map(([v, l]) => (
            <button key={v} onClick={() => setStatusFilter(v)} className={`px-3 py-1.5 font-semibold ${statusFilter === v ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`} data-testid={`filter-${v}`}>{l}</button>
          ))}
        </div>
        <Select value={channelFilter} onValueChange={setChannelFilter}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-channel"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los canales</SelectItem>
            {CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-500 ml-auto">{filtered.length} de {ads.length} ads</span>
      </section>

      {/* Ads list */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ads-grid">
        {filtered.length === 0 && (
          <div className="lg:col-span-2 dotted-bg py-14 text-center text-zinc-400 text-sm">
            {ads.length === 0 ? "Aún no has creado ningún anuncio. Empieza con Nuevo anuncio ↗" : "Ningún ad coincide con los filtros"}
          </div>
        )}
        {filtered.map((ad) => {
          const chan = CHANNELS.find((c) => c.v === ad.channel) || CHANNELS[4];
          const ChanIcon = chan.Icon;
          const st = STATUS_META[ad.status] || STATUS_META.active;
          const canFinish = ad.status === "active" || ad.status === "scheduled";
          const canRegisterResults = (ad.status === "expired" || ad.status === "finished_manual") && !ad.results;
          const hasResults = !!ad.results;
          return (
            <div key={ad.id} className="border border-zinc-200 bg-white p-5 rounded-sm hover:shadow-sm transition-shadow" data-testid={`ad-card-${ad.id}`}>
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: chan.color + "20", color: chan.color }}>
                      <ChanIcon size={11} weight="bold"/>{ad.channel === "other" ? (ad.custom_channel || "Otro") : chan.label}
                    </span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: st.color + "20", color: st.color }}>{st.label}</span>
                  </div>
                  <h3 className="font-heading font-bold text-base leading-tight truncate">{ad.title}</h3>
                  {ad.product_name && (
                    <div className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-1"><Package size={10}/> {ad.product_name}</div>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {canRegisterResults && (
                    <button onClick={() => openResults(ad)} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 bg-[#00873D] hover:bg-[#006b30] text-white rounded" title="Registrar resultados reales" data-testid={`results-ad-${ad.id}`}>
                      <ClipboardText size={11} weight="bold"/> Registrar
                    </button>
                  )}
                  {hasResults && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded" style={{ background: "#00873D20", color: "#00873D" }} title={`ROAS: ${ad.results.roas || "—"}x`}>
                      <CheckCircle size={11} weight="fill"/> Con resultados
                    </span>
                  )}
                  {canFinish && (
                    <button onClick={() => setToFinish(ad)} className="p-1.5 text-zinc-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="Finalizar manualmente" data-testid={`finish-ad-${ad.id}`}>
                      <StopCircle size={14}/>
                    </button>
                  )}
                  <button onClick={() => openEdit(ad)} className="p-1.5 text-zinc-400 hover:text-[#002FA7] hover:bg-zinc-100 rounded" data-testid={`edit-ad-${ad.id}`}>
                    <PencilSimple size={14}/>
                  </button>
                  <button onClick={() => setToDelete(ad)} className="p-1.5 text-zinc-400 hover:text-[#E0000F] hover:bg-red-50 rounded" data-testid={`del-ad-${ad.id}`}>
                    <Trash size={14}/>
                  </button>
                </div>
              </div>

              {/* Dates + Progress */}
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-zinc-500 inline-flex items-center gap-1"><Calendar size={11}/> {ad.start_date} → {ad.end_date}</span>
                  <span className="font-mono-data text-zinc-700">
                    {ad.status === "scheduled" && `Empieza en ${Math.abs(ad.days_remaining)}d`}
                    {ad.status === "active" && `${ad.days_remaining}d restantes`}
                    {ad.status === "expired" && `Vencido hace ${Math.abs(ad.days_remaining)}d`}
                    {ad.status === "finished_manual" && "Finalizado"}
                  </span>
                </div>
                <div className="h-1.5 w-full bg-zinc-100 rounded overflow-hidden">
                  <div className="h-full" style={{ width: `${Math.min(100, ad.progress_pct)}%`, background: st.color }}/>
                </div>
                <div className="text-[10px] text-zinc-400 font-mono-data flex justify-between">
                  <span>{ad.days_elapsed}/{ad.days_active} días</span>
                  <span>{Math.round(ad.progress_pct)}% completado</span>
                </div>
              </div>

              {/* Money block */}
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div className="border border-zinc-200 bg-zinc-50/50 p-2 rounded">
                  <div className="text-[9px] uppercase font-mono-data tracking-wider text-zinc-500">Costo/día</div>
                  <div className="numeric text-sm font-bold text-zinc-800">MX${fmt(ad.cost_per_day)}</div>
                </div>
                <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 p-2 rounded">
                  <div className="text-[9px] uppercase font-mono-data tracking-wider text-[#E0000F]/80">Gastado</div>
                  <div className="numeric text-sm font-bold text-[#E0000F]">MX${fmt(ad.accumulated_spend)}</div>
                  <div className="text-[9px] text-zinc-400">/{fmt(ad.total_budget)}</div>
                </div>
                <div className="border border-[#00873D]/30 bg-[#00873D]/5 p-2 rounded">
                  <div className="text-[9px] uppercase font-mono-data tracking-wider text-[#00873D]/80">Proyección</div>
                  <div className="numeric text-sm font-bold text-[#00873D]">MX${fmt(ad.projected_revenue)}</div>
                  {ad.estimated_profit != null && (
                    <div className={`text-[9px] ${ad.estimated_profit >= 0 ? "text-[#00873D]/80" : "text-[#E0000F]"}`}>
                      util. MX${fmt(ad.estimated_profit)}
                    </div>
                  )}
                </div>
              </div>

              {/* Meta */}
              <div className="mt-3 flex items-center gap-3 flex-wrap text-[11px] text-zinc-500">
                {ad.bank_name && <span className="inline-flex items-center gap-1"><Bank size={11}/> {ad.bank_name}</span>}
                <span className="inline-flex items-center gap-1"><ChartLineUp size={11}/> {ad.projected_units} unidades × MX${fmt(ad.projected_unit_price)}</span>
              </div>
              {ad.notes && <div className="mt-2 text-[11px] text-zinc-500 italic border-l-2 border-zinc-200 pl-2 line-clamp-2">{ad.notes}</div>}

              {/* Resultados reales (Fase 2) */}
              {hasResults && (
                <div className="mt-4 pt-3 border-t border-zinc-200" data-testid={`ad-results-${ad.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-mono-data uppercase tracking-widest text-[#00873D] inline-flex items-center gap-1.5">
                      <CheckCircle size={12} weight="fill"/> Resultados reales
                    </div>
                    {ad.results.roas != null && (
                      <span className={`inline-flex items-baseline gap-1 text-xs font-heading font-bold px-2 py-1 rounded ${ad.results.roas >= 1 ? "bg-[#00873D] text-white" : "bg-[#E0000F] text-white"}`}>
                        ROAS <span className="numeric text-base">{ad.results.roas.toFixed(2)}x</span>
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                    <div className="border border-zinc-200 bg-zinc-50/50 p-1.5 rounded">
                      <div className="text-[9px] uppercase text-zinc-500">Vendido</div>
                      <div className="numeric text-sm font-bold text-[#00873D]">MX${fmt(ad.results.revenue_mxn)}</div>
                      <div className="text-[9px] text-zinc-400">{ad.results.units_sold} u.</div>
                    </div>
                    <div className="border border-zinc-200 bg-zinc-50/50 p-1.5 rounded">
                      <div className="text-[9px] uppercase text-zinc-500">Gastado real</div>
                      <div className="numeric text-sm font-bold text-[#E0000F]">MX${fmt(ad.results.actual_spend_mxn)}</div>
                      {ad.results.cost_per_sale != null && (
                        <div className="text-[9px] text-zinc-400">MX${fmt(ad.results.cost_per_sale)}/venta</div>
                      )}
                    </div>
                    <div className="border border-zinc-200 bg-zinc-50/50 p-1.5 rounded">
                      <div className="text-[9px] uppercase text-zinc-500">vs proyección</div>
                      <div className={`numeric text-sm font-bold ${ad.results.revenue_delta_mxn >= 0 ? "text-[#00873D]" : "text-[#E0000F]"}`}>
                        {ad.results.revenue_delta_mxn >= 0 ? "+" : ""}MX${fmt(ad.results.revenue_delta_mxn)}
                      </div>
                    </div>
                    <div className="border border-zinc-200 bg-zinc-50/50 p-1.5 rounded">
                      <div className="text-[9px] uppercase text-zinc-500">Utilidad real</div>
                      <div className={`numeric text-sm font-bold ${(ad.results.real_profit_mxn ?? 0) >= 0 ? "text-[#00873D]" : "text-[#E0000F]"}`}>
                        {ad.results.real_profit_mxn != null ? `MX$${fmt(ad.results.real_profit_mxn)}` : "—"}
                      </div>
                    </div>
                  </div>
                  {(ad.results.messages_received > 0 || ad.results.interested_clients > 0 || ad.results.closed_deals > 0) && (
                    <div className="mt-2 flex items-center gap-3 flex-wrap text-[10px] text-zinc-500">
                      {ad.results.messages_received > 0 && <span>💬 {ad.results.messages_received} msjs</span>}
                      {ad.results.interested_clients > 0 && <span>👥 {ad.results.interested_clients} interesados</span>}
                      {ad.results.closed_deals > 0 && <span>✅ {ad.results.closed_deals} cerradas</span>}
                    </div>
                  )}
                  {ad.results.comments && (
                    <div className="mt-2 text-[11px] text-zinc-600 italic border-l-2 border-[#00873D]/40 pl-2">{ad.results.comments}</div>
                  )}
                  {ad.results.evidence_url && (
                    <a href={ad.results.evidence_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#002FA7] hover:underline" data-testid={`ad-evidence-${ad.id}`}>
                      <ImageSquare size={12}/> Ver evidencia
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Form modal */}
      <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setEditingId(null); setForm(EMPTY); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ad-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editingId ? "Editar anuncio" : "Nuevo anuncio"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Título *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="ej. Dell Latitude 5480 · Marketplace" data-testid="ad-title" required/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Canal *</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger className="mt-1" data-testid="ad-channel"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {CHANNELS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.channel === "other" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nombre del canal *</Label>
                  <Input value={form.custom_channel} onChange={(e) => setForm({ ...form, custom_channel: e.target.value })} className="mt-1" placeholder="Google Ads, LinkedIn, X..." data-testid="ad-custom-channel"/>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Fecha inicio *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="ad-start"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Fecha fin *</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="ad-end"/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Costo por día (MXN) *</Label>
                <Input type="number" step="0.01" min="0" value={form.cost_per_day} onChange={(e) => setForm({ ...form, cost_per_day: e.target.value })} className="mt-1 numeric" data-testid="ad-cost-per-day"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Banco pagador</Label>
                <Select value={form.bank_id || "_none"} onValueChange={(v) => setForm({ ...form, bank_id: v === "_none" ? "" : v })}>
                  <SelectTrigger className="mt-1" data-testid="ad-bank"><SelectValue placeholder="Sin banco"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Sin banco —</SelectItem>
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.currency ? ` · ${b.currency}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Producto promocionado (opcional)</Label>
              <Select value={form.product_id || "_none"} onValueChange={(v) => setForm({ ...form, product_id: v === "_none" ? "" : v, projected_unit_price: "" })}>
                <SelectTrigger className="mt-1" data-testid="ad-product"><SelectValue placeholder="Sin producto"/></SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="_none">— Sin producto —</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Unidades proyectadas</Label>
                <Input type="number" min="0" value={form.projected_units} onChange={(e) => setForm({ ...form, projected_units: e.target.value })} className="mt-1 numeric" data-testid="ad-units"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Precio por unidad</Label>
                <Input type="number" step="0.01" min="0" value={form.projected_unit_price} onChange={(e) => setForm({ ...form, projected_unit_price: e.target.value })} className="mt-1 numeric" placeholder="auto desde producto" data-testid="ad-unit-price"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Notas internas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" placeholder="Público objetivo, estrategia, links..." data-testid="ad-notes"/>
            </div>

            {/* Live calc preview */}
            <div className="border border-zinc-200 bg-zinc-50/50 rounded p-3">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 mb-1.5">Vista previa · cálculos automáticos</div>
              {(() => {
                const cpd = parseFloat(form.cost_per_day) || 0;
                const s = form.start_date; const e = form.end_date;
                let days = 0;
                if (s && e) {
                  const d1 = new Date(s + "T12:00:00"); const d2 = new Date(e + "T12:00:00");
                  days = Math.max(0, Math.round((d2 - d1) / 86400000) + 1);
                }
                const budget = cpd * days;
                const units = parseInt(form.projected_units) || 0;
                const price = parseFloat(form.projected_unit_price) || 0;
                const revenue = units * price;
                return (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div><div className="text-[9px] text-zinc-500">Días activos</div><div className="numeric font-bold text-zinc-800">{days}</div></div>
                    <div><div className="text-[9px] text-zinc-500">Presupuesto total</div><div className="numeric font-bold text-[#E0000F]">MX${fmt(budget)}</div></div>
                    <div><div className="text-[9px] text-zinc-500">Proyección venta</div><div className="numeric font-bold text-[#00873D]">MX${fmt(revenue)}</div></div>
                  </div>
                );
              })()}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#E91E63] hover:bg-[#c2185b]" data-testid="ad-save">
                {saving ? "Guardando…" : (editingId ? "Guardar cambios" : "Crear anuncio")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar anuncio?</AlertDialogTitle>
            <AlertDialogDescription>Se archivará <b>{toDelete?.title}</b>. Se puede recuperar más adelante desde la base de datos.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#E0000F] hover:bg-[#B00000]" data-testid="confirm-delete-ad">Archivar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual finish confirmation */}
      <AlertDialog open={!!toFinish} onOpenChange={(o) => { if (!o) setToFinish(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Finalizar anuncio manualmente?</AlertDialogTitle>
            <AlertDialogDescription>Se marcará <b>{toFinish?.title}</b> como Finalizado. Podrás registrar los resultados reales del anuncio en la próxima fase del módulo.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmFinish} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-finish-ad">Finalizar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Register results modal (Fase 2) */}
      <Dialog open={!!resultsAd} onOpenChange={(o) => { if (!o) { setResultsAd(null); setResultsForm(null); } }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="ad-results-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
              <ClipboardText size={22} weight="duotone" className="text-[#00873D]"/> Registrar resultados
            </DialogTitle>
          </DialogHeader>
          {resultsAd && resultsForm && (
            <form onSubmit={submitResults} className="space-y-4">
              {/* Ad summary header */}
              <div className="bg-zinc-50 border border-zinc-200 p-3 rounded flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Anuncio</div>
                  <div className="font-semibold text-zinc-800 truncate">{resultsAd.title}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    Proyectado: <b className="numeric">MX${fmt(resultsAd.projected_revenue)}</b> · Presupuesto: <b className="numeric">MX${fmt(resultsAd.total_budget)}</b>
                  </div>
                </div>
              </div>

              {/* Real numbers */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Unidades vendidas *</Label>
                  <Input type="number" min="0" value={resultsForm.units_sold} onChange={(e) => setResultsForm({ ...resultsForm, units_sold: e.target.value })} className="mt-1 numeric" data-testid="results-units"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Monto vendido</Label>
                  <div className="flex gap-1 mt-1">
                    <Input type="number" step="0.01" min="0" value={resultsForm.revenue_amount} onChange={(e) => setResultsForm({ ...resultsForm, revenue_amount: e.target.value })} className="numeric" data-testid="results-revenue"/>
                    <Select value={resultsForm.revenue_currency} onValueChange={(v) => setResultsForm({ ...resultsForm, revenue_currency: v })}>
                      <SelectTrigger className="w-20"><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Gasto real *</Label>
                  <div className="flex gap-1 mt-1">
                    <Input type="number" step="0.01" min="0" value={resultsForm.actual_spend} onChange={(e) => setResultsForm({ ...resultsForm, actual_spend: e.target.value })} className="numeric" data-testid="results-spend"/>
                    <Select value={resultsForm.actual_spend_currency} onValueChange={(v) => setResultsForm({ ...resultsForm, actual_spend_currency: v })}>
                      <SelectTrigger className="w-20"><SelectValue/></SelectTrigger>
                      <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Engagement metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Mensajes 💬</Label>
                  <Input type="number" min="0" value={resultsForm.messages_received} onChange={(e) => setResultsForm({ ...resultsForm, messages_received: e.target.value })} className="mt-1 numeric" data-testid="results-messages"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Interesados 👥</Label>
                  <Input type="number" min="0" value={resultsForm.interested_clients} onChange={(e) => setResultsForm({ ...resultsForm, interested_clients: e.target.value })} className="mt-1 numeric" data-testid="results-interested"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Cerradas ✅</Label>
                  <Input type="number" min="0" value={resultsForm.closed_deals} onChange={(e) => setResultsForm({ ...resultsForm, closed_deals: e.target.value })} className="mt-1 numeric" data-testid="results-closed"/>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">Comentarios de desempeño</Label>
                <Textarea rows={2} value={resultsForm.comments} onChange={(e) => setResultsForm({ ...resultsForm, comments: e.target.value })} className="mt-1" placeholder="¿Qué funcionó? ¿Qué no?" data-testid="results-comments"/>
              </div>

              {/* Evidence upload */}
              <div>
                <Label className="text-xs uppercase tracking-wider">Evidencia (opcional)</Label>
                <div className="mt-1 flex items-start gap-3">
                  {resultsForm.evidence_url ? (
                    <div className="relative">
                      <img src={resultsForm.evidence_url} alt="evidencia" className="w-32 h-32 object-cover rounded border border-zinc-200"/>
                      <button type="button" onClick={() => setResultsForm({ ...resultsForm, evidence_url: "" })} className="absolute -top-2 -right-2 bg-white border border-zinc-200 rounded-full p-0.5 hover:bg-red-50" data-testid="results-remove-evidence">
                        <X size={12} className="text-[#E0000F]"/>
                      </button>
                    </div>
                  ) : (
                    <label className="inline-flex items-center gap-2 text-xs border border-dashed border-zinc-300 rounded px-3 py-2 cursor-pointer hover:bg-zinc-50" data-testid="results-upload-evidence">
                      <UploadSimple size={14}/>
                      {uploadingEvidence ? "Subiendo…" : "Subir captura del anuncio"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadEvidence(e.target.files?.[0])} disabled={uploadingEvidence}/>
                    </label>
                  )}
                  <p className="text-[10px] text-zinc-400 flex-1">Captura del anuncio publicado, link o resultado en Facebook/TikTok. Guardado en Cloudinary.</p>
                </div>
              </div>

              {/* Register expense toggle */}
              <div className="border border-zinc-200 bg-zinc-50/50 rounded p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={resultsForm.register_expense} onChange={(e) => setResultsForm({ ...resultsForm, register_expense: e.target.checked })} className="w-4 h-4 mt-0.5 accent-[#E91E63]" data-testid="results-register-expense"/>
                  <div className="text-xs">
                    <div className="font-semibold text-zinc-800">Registrar gasto real y descontar del banco</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Al activarlo se crea un gasto en la categoría <b>Publicidad · Ads</b> por MX${fmt(resultsForm.actual_spend)} y se descuenta del banco seleccionado.</div>
                  </div>
                </label>
                {resultsForm.register_expense && (
                  <div className="pl-6">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Banco pagador *</Label>
                    <Select value={resultsForm.bank_id || "_none"} onValueChange={(v) => setResultsForm({ ...resultsForm, bank_id: v === "_none" ? "" : v })}>
                      <SelectTrigger className="mt-1 h-8" data-testid="results-bank"><SelectValue placeholder="Selecciona banco"/></SelectTrigger>
                      <SelectContent>
                        {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.currency ? ` · ${b.currency}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* ROAS preview */}
              {(() => {
                const rev = parseFloat(resultsForm.revenue_amount) || 0;
                const spend = parseFloat(resultsForm.actual_spend) || 0;
                const roas = spend > 0 ? rev / spend : 0;
                return spend > 0 ? (
                  <div className="bg-[#E91E63]/5 border border-[#E91E63]/30 p-3 rounded flex items-center justify-between">
                    <span className="text-xs text-zinc-600">ROAS estimado</span>
                    <span className={`inline-flex items-baseline gap-1 text-xl font-heading font-bold ${roas >= 1 ? "text-[#00873D]" : "text-[#E0000F]"}`}>
                      <span className="numeric">{roas.toFixed(2)}</span><span className="text-sm">x</span>
                    </span>
                  </div>
                ) : null;
              })()}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setResultsAd(null); setResultsForm(null); }}>Cancelar</Button>
                <Button type="submit" disabled={savingResults} className="bg-[#00873D] hover:bg-[#006b30]" data-testid="results-save">
                  {savingResults ? "Guardando…" : "Guardar resultados"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
